import { LoginWorkflowLayout } from "components/app/login-workflow-layout";
import { Checkbox } from "components/ui/checkbox";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import React, { ChangeEvent, FormEvent, MouseEvent, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { login } from "store/actions/session";
import { ApplicationState } from "store/state";
import { LoginCredentials } from "util/browser";
import { performDomainRedirect } from "util/domain-redirect";
import { navigate } from "../../store/actions";

export const Login: React.FC = () => {
    const dispatch = useDispatch();

    const loginFailureMessage = useSelector(
        (state: ApplicationState) => state.login.loginFailureMessage,
    );

    const [credentials, setCredentials] = useState<LoginCredentials>({
        email: "",
        password: "",
        longTermLogin: false,
    });

    const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

    const queryClient = useQueryClient();

    useEffect(() => {
        performDomainRedirect();

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("passwordReset") === "success") {
            setPasswordResetSuccess(true);
            window.history.replaceState({}, "", "/login");
        }
    }, []);

    const handleSubmit = (e: FormEvent<HTMLFormElement> | null): void => {
        if (e) {
            e.preventDefault();
        }
        login(credentials, queryClient, dispatch);
    };

    const changeEmail = (e: ChangeEvent<HTMLInputElement>): void => {
        setCredentials(prev => ({ ...prev, email: e.target.value }));
    };

    const changePassword = (e: ChangeEvent<HTMLInputElement>): void => {
        setCredentials(prev => ({ ...prev, password: e.target.value }));
    };

    const onKeepLoggedInCheckbox = (e: ChangeEvent<HTMLInputElement>): void => {
        setCredentials(prev => ({ ...prev, longTermLogin: e.target.checked }));
    };

    const resetPassword = (e: MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();
        navigate("/account/reset-password");
    };

    const shouldUseAnotherBrowser = (): boolean => {
        const userAgent = ((navigator && navigator.userAgent) || "").toLowerCase();
        const vendor = ((navigator && navigator.vendor) || "").toLowerCase();
        const isFirefox = /(?:firefox|fxios)\/(\d+)/.test(userAgent);
        const isChrome = /google inc/.test(vendor)
            ? /(?:chrome|crios)\/(\d+)/.test(userAgent)
            : false;
        const isMobile =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                userAgent,
            );
        const supportsTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

        return !isMobile && !isFirefox && !isChrome && !supportsTouch;
    };

    const browserGreenlight = (): JSX.Element | null => {
        if (shouldUseAnotherBrowser()) {
            return (
                <div className="mb-6 rounded-md border border-yellow-300 bg-yellow-50 p-4">
                    <h3 className="text-sm font-medium text-yellow-800">
                        We recommend a different browser
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                        <p>
                            For the best experience, we strongly recommend you download{" "}
                            <a
                                href="https://www.google.com/chrome/browser"
                                target="_blank"
                                rel="noreferrer"
                                className="underline hover:no-underline">
                                Google Chrome
                            </a>{" "}
                            or{" "}
                            <a
                                href="https://www.mozilla.org/download"
                                target="_blank"
                                rel="noreferrer"
                                className="underline hover:no-underline">
                                Mozilla Firefox
                            </a>
                            .
                        </p>
                        <p className="mt-2">
                            If you would like to proceed with your current browser you can
                            still login below.
                        </p>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <LoginWorkflowLayout title="Sign in to your account">
            <form onSubmit={handleSubmit} className="space-y-6">
                {browserGreenlight()}
                {passwordResetSuccess && (
                    <InlineBanner type="success">
                        Your password has been reset successfully. Please log in with your
                        new password.
                    </InlineBanner>
                )}
                {loginFailureMessage ? (
                    <InlineBanner type="error">{loginFailureMessage}</InlineBanner>
                ) : null}
                <Input
                    type="email"
                    id="email"
                    name="email"
                    required
                    autoComplete="email"
                    value={credentials.email}
                    onChange={changeEmail}
                    label="Email address"
                />
                <Input
                    type="password"
                    id="password"
                    name="password"
                    required
                    autoComplete="current-password"
                    value={credentials.password}
                    onChange={changePassword}
                    label="Password"
                />
                <div className="flex items-center justify-between">
                    <div className="flex-shrink-0">
                        <Checkbox
                            id="remember-me"
                            name="remember-me"
                            checked={credentials.longTermLogin}
                            onChange={onKeepLoggedInCheckbox}
                            label="Keep me logged in"
                            className=""
                        />
                    </div>
                    <div className="text-sm flex-shrink-0">
                        <a href="#" onClick={resetPassword} className={defaultLinkStyle}>
                            Forgot password?
                        </a>
                    </div>
                </div>
                <PrimaryButton type="submit" id="signin" className="w-full">
                    Sign in
                </PrimaryButton>
            </form>
        </LoginWorkflowLayout>
    );
};
